import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { storeService } from '../../services/store.service';
import { CreatorStore, UpdateCreatorStoreData } from '../../types/creatorStore.types';
import { requestCache } from '../../utils/requestCache';

export interface CreatorStoreState {
  creatorStore: CreatorStore | null;
  publicStores: Record<string, CreatorStore>;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: CreatorStoreState = {
  creatorStore: null,
  publicStores: {},
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch my creator store settings
 * GET /store/settings
 */
export const fetchMyCreatorStore = createAsyncThunk(
  'creatorStore/fetchMyCreatorStore',
  async (_, { rejectWithValue }) => {
    try {
      const response = await storeService.getMyStore();
      if (response.success) {
        return response.data.store;
      }
      return rejectWithValue('error' in response ? response.error.message : 'Failed to fetch creator store');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || error.message || 'Failed to fetch creator store');
    }
  }
);

/**
 * Update my creator store settings
 * PATCH /store/settings
 */
export const updateMyCreatorStore = createAsyncThunk(
  'creatorStore/updateMyCreatorStore',
  async (data: UpdateCreatorStoreData, { rejectWithValue }) => {
    try {
      const response = await storeService.updateStore(data);
      if (response.success) {
        // Clear cache for fetchMyCreatorStore
        requestCache.clear();
        return response.data.store;
      }
      return rejectWithValue('error' in response ? response.error.message : 'Failed to update creator store');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || error.message || 'Failed to update creator store');
    }
  }
);

/**
 * Fetch public creator store by user ID
 * GET /store/user/:userId
 */
export const fetchPublicCreatorStore = createAsyncThunk(
  'creatorStore/fetchPublicCreatorStore',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await storeService.getPublicStore(userId);
      if (response.success) {
        return { userId, creatorStore: response.data.store };
      }
      // Handle 404 - store not found
      if ('error' in response && response.error.code === 404) {
        return { userId, creatorStore: null };
      }
      return rejectWithValue('error' in response ? response.error.message : 'Failed to fetch public creator store');
    } catch (error: any) {
      // Return null for 404 errors (store not found)
      if (error.response?.status === 404) {
        return { userId, creatorStore: null };
      }
      return rejectWithValue(error.response?.data?.error?.message || error.message || 'Failed to fetch public creator store');
    }
  }
);

const creatorStoreSlice = createSlice({
  name: 'creatorStore',
  initialState,
  reducers: {
    clearCreatorStoreError: (state) => {
      state.error = null;
    },
    clearCreatorStore: (state) => {
      state.creatorStore = null;
      state.publicStores = {};
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch My Creator Store
      .addCase(fetchMyCreatorStore.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMyCreatorStore.fulfilled, (state, action: PayloadAction<CreatorStore>) => {
        state.isLoading = false;
        state.creatorStore = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchMyCreatorStore.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update My Creator Store
      .addCase(updateMyCreatorStore.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMyCreatorStore.fulfilled, (state, action: PayloadAction<CreatorStore>) => {
        state.isLoading = false;
        state.creatorStore = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(updateMyCreatorStore.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Public Creator Store
      .addCase(fetchPublicCreatorStore.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicCreatorStore.fulfilled, (state, action: PayloadAction<{ userId: string; creatorStore: CreatorStore | null }>) => {
        state.isLoading = false;
        if (action.payload.creatorStore) {
          state.publicStores[action.payload.userId] = action.payload.creatorStore;
        }
        state.error = null;
      })
      .addCase(fetchPublicCreatorStore.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCreatorStoreError, clearCreatorStore } = creatorStoreSlice.actions;
export default creatorStoreSlice.reducer;
